import React, { useState } from 'react'
import { CircularProgress } from '@mui/material'
import { SurfacePalette, status, type as typo } from '../tokens'
import { PacketModalShell, modalGhostButtonStyle } from './PacketModalShell'

type Props = {
  palette: SurfacePalette
  creativeName: string
  onConfirm: () => Promise<void>
  onClose: () => void
}

/**
 * Confirms removal of a creative's client approval. Palette-aware so it matches
 * the surface it is invoked from (light Edit, dark Present).
 */
export function ClientApprovalRemoveConfirm({ palette, creativeName, onConfirm, onClose }: Props) {
  const [removing, setRemoving] = useState(false)

  const handleConfirm = async () => {
    if (removing) return
    setRemoving(true)
    try {
      await onConfirm()
      onClose()
    } finally {
      setRemoving(false)
    }
  }

  return (
    <PacketModalShell
      palette={palette}
      title="Remove approval"
      subtitle={`“${creativeName}”`}
      onClose={onClose}
    >
      <p style={{ margin: 0, fontSize: typo.size.body, color: palette.textMuted, lineHeight: 1.5 }}>
        Remove the client approval for this creative? It will return to its previous status and can
        be approved again.
      </p>

      <div style={{ display: 'flex', gap: 12 }}>
        <button
          type="button"
          onClick={onClose}
          disabled={removing}
          style={{ ...modalGhostButtonStyle(palette), flex: 1 }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={removing}
          style={{
            flex: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            background: status.danger,
            color: '#fff',
            border: `1px solid ${status.danger}`,
            borderRadius: 6,
            padding: '8px 16px',
            fontSize: typo.size.body,
            fontWeight: typo.weight.semibold,
            cursor: removing ? 'default' : 'pointer',
            opacity: removing ? 0.6 : 1,
          }}
        >
          {removing ? <CircularProgress size={16} style={{ color: '#fff' }} /> : 'Remove approval'}
        </button>
      </div>
    </PacketModalShell>
  )
}
